import { Event, magiclinkUrlUser, Organization } from '@goldcast/api/backend';
import logger from '@goldcast/logger';

import store from '@/store';
import { BASE_URL } from '@/utils/constants';
import { getEnv } from '@/utils/env';

export function getRegistrationLink(
  eventId: String,
  orgShortName: String,
  registrationAppVanityDomainSettings: Organization['registration_app_vanity_domain_settings'],
  registrationAppDomain: Event['registration_app_domain'],
  eventType: 'events' | 'webinar'
) {
  const domain = (registrationAppVanityDomainSettings as Record<string, any>[])?.filter?.(
    setting => setting.is_default && setting.is_verified
  )?.[0]?.domain;
  if (registrationAppDomain) {
    return registrationAppDomain;
  }
  if (domain) {
    return `${domain}/${eventType}/${eventId}`;
  }

  const baseUrl = eventType === 'events' ? BASE_URL.REGISTRATION : BASE_URL.WEBINAR_REGISTRATION;
  return `https://${orgShortName}${baseUrl}${eventId}`;
}

export function getOnDemandPageLink(
  eventId: String,
  orgShortName: String,
  on_demand_app_url: Event['on_demand_app_url']
) {
  if (on_demand_app_url) {
    return on_demand_app_url;
  }

  const baseUrl = getEnv('VUE_APP_ONDEMAND_PAGE_URL');
  return `https://${orgShortName}${baseUrl}/on-demand/${eventId}`;
}

export function getSeriesRegistrationLink(
  seriesId: String,
  orgShortName: String,
  registrationAppVanityDomainSettings: Organization['registration_app_vanity_domain_settings'],
  seriesType: 'series' | 'repeating-event',
  seriesVanityUrl: string
) {
  if (seriesVanityUrl) {
    return seriesVanityUrl;
  }
  const domain = (registrationAppVanityDomainSettings as Record<string, any>[])?.filter?.(
    setting => setting.is_default && setting.is_verified
  )?.[0]?.domain;
  if (domain) {
    return `${domain}/${seriesType}/${seriesId}`;
  }

  const seriesRegistrationBaseURL =
    seriesType === 'series' ? BASE_URL.SERIES_REGISTRATION : BASE_URL.REPEATING_EVENT_REGISTRATION;
  return `https://${orgShortName}${seriesRegistrationBaseURL}${seriesId}`;
}

export async function getEventAppMagicLink(eventId: string, customDomain: Event['custom_domain']): Promise<string> {
  try {
    const email = store?.getters['user/getCurrentUser']?.email;
    const response = await magiclinkUrlUser({
      queryParams: {
        email,
        event_id: eventId
      }
    });
    const magicLink = response.url;
    if (customDomain) {
      const url = new URL(magicLink);
      const modifiedMagicLink = `${customDomain}${url.pathname}${url.search}`;
      return modifiedMagicLink;
    }
    const eventAppDomain = getEnv('VUE_APP_API_EVENT_URL');
    if (eventAppDomain) {
      const url = new URL(magicLink);
      const modifiedMagicLink = `${eventAppDomain}${url.pathname}${url.search}`;
      return modifiedMagicLink;
    }
    return magicLink;
  } catch (error) {
    logger.error("Couldn't fetch magic link url", error);
    return ``;
  }
}

export function getEventLink(eventId: String, eventAppDomain: Event['custom_domain']): string {
  if (eventAppDomain) {
    return `${eventAppDomain}/splash/${eventId}`;
  }

  return `${getEnv('VUE_APP_API_EVENT_URL')}/splash/${eventId}`;
}

export function getAgendaLink(eventId: String, agendaId: String) {
  return `${getEnv('VUE_APP_API_EVENT_URL')}/e/${eventId}/stage/broadcast/${agendaId}`;
}

function buildInPersonManagerLink(magicLinkUrl: string, email?: string, extraParams?: string): string {
  if (!magicLinkUrl) {
    return '';
  }

  try {
    const url = new URL(magicLinkUrl);
    let pathname = url.pathname;
    const pathSegments = pathname.split('/');
    if (email && pathSegments[1] === 'auth' && pathSegments[2] === 'link' && pathSegments[3] && pathSegments[4]) {
      pathSegments[3] = encodeURIComponent(email);
      pathname = pathSegments.join('/');
    }
    const search = extraParams ? `${url.search}&${extraParams}` : url.search;
    return `${getEnv('VUE_APP_INPERSONMANAGER')}${pathname}${search}`;
  } catch (error) {
    logger.error('Invalid URL', error);
    return '';
  }
}

export function getInPersonManagerLink(magicLinkUrl: string, email?: string): string {
  return buildInPersonManagerLink(magicLinkUrl, email);
}

export function getInPersonManagerSelfCheckInLink(magicLinkUrl: string, email?: string): string {
  return buildInPersonManagerLink(magicLinkUrl, email, 'selfCheckIn=true');
}

export function getOnDemandEmbedScript(eventId: string): string {
  return (
    `<div id="player-${eventId}" style="aspect-ratio: 4 / 2.3; width: 100%;" ></div>\n<script src="https://ondemand${BASE_URL.ON_DEMAND_EMBED}${eventId}"><` +
    `/script>`
  );
}

export function getEventSSOLink(eventId: string, allowedEmailDomains: string[]): string {
  const domains = Array.isArray(allowedEmailDomains) ? allowedEmailDomains : [];
  const emailDomainsParams = domains.map(domain => `email_domains=${encodeURIComponent(domain)}`).join('&');
  return `${getEnv('VUE_APP_SSO_BACKEND_URL')}/auth/sso_event_redirect/?event=${eventId}&${emailDomainsParams}`;
}
